// src/pages/LandingPage/Plataforma/Correspondencia/RegistrarEncomiendaClienteModal.tsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
    X, Package, User, FileText, CheckCircle2, MessageSquare, 
    Printer, UserCheck, AlertCircle, RefreshCw, Send,
    ArrowRight, Copy, Check
} from 'lucide-react';
import { createCorrespondencia, Correspondencia, CorrespondenciaCreate } from '../../../../services/correspondencia';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';


interface RegistrarEncomiendaClienteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

const RegistrarEncomiendaClienteModal: React.FC<RegistrarEncomiendaClienteModalProps> = ({
    isOpen,
    onClose,
    onSave
}) => {
    const { user } = useAuth();
    const { isDark } = useTheme();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // === SECCIÓN 1: ¿QUIÉN DEJA EL PAQUETE? ===
    // true: El cliente titular lo deja personalmente
    // false: El cliente manda a un tercero (mensajero / persona externa)
    const [yoLoDejoPersonalmente, setYoLoDejoPersonalmente] = useState(true);
    const [mensajeroNombre, setMensajeroNombre] = useState('');
    const [mensajeroTelefono, setMensajeroTelefono] = useState('');
    const [mensajeroCiNit, setMensajeroCiNit] = useState('');

    // === SECCIÓN 2: ¿QUIÉN RECOGE EL PAQUETE? (DESTINATARIO) ===
    const [destNombre, setDestNombre] = useState('');
    const [destTelefono, setDestTelefono] = useState('');
    const [destEmail, setDestEmail] = useState('');
    const [destCiNit, setDestCiNit] = useState('');

    // === SECCIÓN 3: DETALLES DEL PAQUETE ===
    const [descripcion, setDescripcion] = useState('');

    // Éxito
    const [creadaExitosa, setCreadaExitosa] = useState<Correspondencia | null>(null);
    const [copiadoLink, setCopiadoLink] = useState(false);
    const [copiadoPin, setCopiadoPin] = useState(false);

    const handleClose = () => {
        onClose();
    };

    // ===== LIFECYCLE =====
    useEffect(() => {
        if (isOpen) {
            setYoLoDejoPersonalmente(true);
            setMensajeroNombre('');
            setMensajeroTelefono('');
            setMensajeroCiNit('');
            setDestNombre('');
            setDestTelefono('');
            setDestEmail('');
            setDestCiNit('');
            setDescripcion('');
            setError(null);
            setCreadaExitosa(null);
            setCopiadoLink(false);
            setCopiadoPin(false);
        }
    }, [isOpen]);

    // Nombre completo del usuario autenticado
    const miNombreCompleto = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';

    // Remitente según la opción
    const getRemitenteNombre = () => {
        if (yoLoDejoPersonalmente) return miNombreCompleto || user?.email || 'Cliente';
        return mensajeroNombre.trim();
    };

    const getRemitenteTelefono = () => {
        if (yoLoDejoPersonalmente) return user?.telefono || '';
        return mensajeroTelefono.trim();
    };

    const getRemitenteCiNit = () => {
        if (yoLoDejoPersonalmente) return user?.ci_nit || '';
        return mensajeroCiNit.trim();
    };

    // ===== SUBMIT =====
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!yoLoDejoPersonalmente && !mensajeroNombre.trim()) {
            setError('Por favor indica el nombre de la persona que mandas a dejar el paquete.');
            return;
        }

        if (!destNombre.trim()) {
            setError('El nombre de la persona que recogerá la encomienda (destinatario) es obligatorio.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload: CorrespondenciaCreate = {
                // Se asocia automáticamente al cliente en sesión
                cliente: user?.id || null,

                // Remitente (quien deja el paquete)
                remitente_nombre: getRemitenteNombre(),
                remitente_telefono: getRemitenteTelefono() || undefined,
                remitente_ci_nit: getRemitenteCiNit() || undefined,

                // Destinatario (quien va a recoger)
                destinatario_nombre: destNombre.trim(),
                destinatario_telefono: destTelefono.trim() || undefined,
                destinatario_email: destEmail.trim() || undefined,
                destinatario_ci_nit: destCiNit.trim() || undefined,

                descripcion: descripcion.trim() || undefined,
                // Nota: Los clientes no toman fotos de comprobante; solo el personal/admin lo hace en recepción
            };

            const created = await createCorrespondencia(payload);
            setCreadaExitosa(created);
            onSave();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            const msg = err?.response?.data;
            if (typeof msg === 'object') {
                const firstKey = Object.keys(msg)[0];
                setError(`${firstKey}: ${Array.isArray(msg[firstKey]) ? msg[firstKey][0] : msg[firstKey]}`);
            } else {
                setError('Ocurrió un error al registrar la encomienda. Por favor intenta nuevamente.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // ===== LINKS DE SEGUIMIENTO & WHATSAPP =====
    const getTrackingUrl = () => {
        if (!creadaExitosa) return '';
        // Preferir token_verificacion criptográfico (no contiene barras)
        const param = creadaExitosa.token_verificacion || "";
        return `${window.location.origin}/seguimiento/${encodeURIComponent(param)}`;
    };

    const generarLinkWhatsAppDestinatario = () => {
        if (!creadaExitosa) return '#';
        const telefono = creadaExitosa.destinatario_telefono || destTelefono || '';
        const nombreDest = creadaExitosa.destinatario_nombre || destNombre || 'Estimado(a)';
        const digits = (telefono || '').replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;
        const trackingUrl = getTrackingUrl();
        const quienDeja = yoLoDejoPersonalmente ? miNombreCompleto : `${mensajeroNombre} (de parte de ${miNombreCompleto})`;

        const mensaje = `Hola *${nombreDest}*, te informamos que *${miNombreCompleto}* ha registrado una encomienda para ti en recepción de *Comarca Coworking* (dejada por ${quienDeja}).\n\n` +
            `📦 *Código de Paquete:* ${creadaExitosa.codigo || `#${creadaExitosa.id}`}\n` +
            `🔐 *PIN Secreto de Retiro:* ${creadaExitosa.pin_seguridad}\n\n` +
            `📱 *Ver proceso de entrega y comprobante QR aquí:*\n${trackingUrl}\n\n` +
            `_Muestra tu QR o dicta tu PIN en recepción para retirarlo cuando gustes._`;

        return `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`;
    };

    const generarLinkWhatsAppMensajero = () => {
        if (!creadaExitosa) return '#';
        const telefono = mensajeroTelefono || '';
        const digits = (telefono || '').replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;
        const trackingUrl = getTrackingUrl();

        const mensaje = `Hola *${mensajeroNombre}*, te comparto los datos de la encomienda que vas a dejar en recepción de *Comarca Coworking* para *${destNombre}*:\n\n` +
            `📦 *Código:* ${creadaExitosa.codigo || `#${creadaExitosa.id}`}\n` +
            `📱 *Enlace de seguimiento:*\n${trackingUrl}\n\n` +
            `_Solo debes indicar en recepción que dejas este paquete de parte de ${miNombreCompleto}._`;

        return `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`;
    };

    const handleCopiarLink = () => {
        const url = getTrackingUrl();
        if (url) {
            navigator.clipboard.writeText(url);
            setCopiadoLink(true);
            setTimeout(() => setCopiadoLink(false), 2000);
        }
    };

    const handleCopiarPin = () => {
        if (creadaExitosa?.pin_seguridad) {
            navigator.clipboard.writeText(creadaExitosa.pin_seguridad);
            setCopiadoPin(true);
            setTimeout(() => setCopiadoPin(false), 2000);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-3 sm:p-4 md:p-6 backdrop-blur-md font-['DM_Sans',sans-serif] overflow-y-auto"
            onClick={handleClose}
        >
            <div 
                className="rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden custom-scroll animate-scale-up my-auto border"
                style={{
                    background: isDark ? '#150a26' : '#ffffff',
                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#f3f4f6',
                    color: isDark ? '#ffffff' : '#111827'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div 
                    className="sticky top-0 z-10 px-6 pt-6 pb-4 border-b rounded-t-3xl flex-shrink-0"
                    style={{
                        background: isDark ? '#150a26' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-gradient-to-tr from-[#61257B] to-[#C1348B] text-white rounded-2xl shadow-sm shadow-purple-900/30">
                                <Package className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                    {creadaExitosa ? '¡Encomienda Registrada!' : 'Mandar Encomienda / Dejar Paquete'}
                                </h3>
                                <p className="text-xs" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                    {creadaExitosa ? 'Comparte el PIN y el enlace con quien lo retirará' : 'Registra tu paquete para dejarlo en custodia en recepción'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-lg transition-colors"
                            style={{
                                color: isDark ? '#cbd5e1' : '#9ca3af',
                                background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                            }}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* VISTA 1: ÉXITO */}
                {creadaExitosa ? (
                    <div className="p-6 space-y-6 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-full">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>

                        <div>
                            <span 
                                className="px-3 py-1 font-mono font-bold text-xs rounded-full uppercase tracking-wider border"
                                style={{
                                    background: isDark ? 'rgba(97, 37, 123, 0.25)' : '#f5f3ff',
                                    color: isDark ? '#ffd97c' : '#61257B',
                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe'
                                }}
                            >
                                {creadaExitosa.codigo || `#${creadaExitosa.id}`}
                            </span>
                            <h4 className="text-2xl font-black mt-2" style={{ color: isDark ? '#ffffff' : '#111827' }}>
                                Encomienda Registrada Exitosamente
                            </h4>
                            <p className="text-xs mt-1" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                {!yoLoDejoPersonalmente ? (
                                    <>Envías a: <span className="font-bold text-amber-500">{mensajeroNombre}</span> a dejarlo &nbsp;•&nbsp; </>
                                ) : (
                                    <>Dejas tú personalmente &nbsp;•&nbsp; </>
                                )}
                                Para retirar por: <span className="font-bold" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>{destNombre}</span>
                            </p>
                        </div>

                        {/* PIN Secreto */}
                        <div 
                            className="p-4 rounded-2xl max-w-sm mx-auto border-2"
                            style={{
                                background: isDark ? 'linear-gradient(to right, rgba(97, 37, 123, 0.25), rgba(193, 52, 139, 0.15))' : 'linear-gradient(to right, #faf5ff, #fdf4ff)',
                                borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#e9d5ff'
                            }}
                        >
                            <span className="text-xs uppercase tracking-wider font-bold block mb-1" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                PIN Secreto de Retiro
                            </span>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-3xl font-mono font-black tracking-widest" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>
                                    {creadaExitosa.pin_seguridad}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleCopiarPin}
                                    className="p-1.5 rounded-lg transition-colors"
                                    style={{ color: isDark ? '#ffd97c' : '#61257B' }}
                                    title="Copiar PIN"
                                >
                                    {copiadoPin ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </div>
                            <span className="text-[11px] block mt-1" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                Comparte este PIN con la persona que recogerá el paquete en recepción.
                            </span>
                        </div>

                        {/* Link de seguimiento público (sin necesidad de login) */}
                        <div 
                            className="p-3.5 rounded-2xl max-w-md mx-auto text-left border"
                            style={{
                                background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'
                            }}
                        >
                            <span className="text-[10px] uppercase tracking-wider font-bold block mb-1.5" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                🔗 Enlace de Seguimiento Público (No requiere usuario)
                            </span>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    readOnly
                                    value={getTrackingUrl()}
                                    className="flex-1 px-2.5 py-1.5 rounded-lg text-[11px] font-mono outline-none border"
                                    style={{
                                        background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                        color: isDark ? '#ffffff' : '#374151',
                                        borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#e5e7eb'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleCopiarLink}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg shadow-sm border transition-colors"
                                    style={{
                                        color: isDark ? '#ffd97c' : '#61257B',
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                        borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#bfdbfe'
                                    }}
                                >
                                    {copiadoLink ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                    <span>{copiadoLink ? '¡Copiado!' : 'Copiar'}</span>
                                </button>
                            </div>
                        </div>

                        {/* Botones de Envío por WhatsApp */}
                        <div className="space-y-3 max-w-md mx-auto text-left">
                            <label className="block text-xs font-bold uppercase tracking-wider text-center" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                Enviar Notificación por WhatsApp
                            </label>

                            {/* WhatsApp a Destinatario */}
                            <a
                                href={generarLinkWhatsAppDestinatario()}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3.5 px-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-2.5">
                                    <MessageSquare className="w-5 h-5 fill-white" />
                                    <div>
                                        <div className="text-sm font-bold">Enviar al Destinatario (Quién recogerá)</div>
                                        <div className="text-[11px] font-normal text-white/90">
                                            {destNombre} {destTelefono ? `(${destTelefono})` : ''}
                                        </div>
                                    </div>
                                </div>
                                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>

                            {/* WhatsApp al Mensajero (si no lo deja personalmente) */}
                            {!yoLoDejoPersonalmente && (
                                <a
                                    href={generarLinkWhatsAppMensajero()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-3.5 px-4 bg-[#61257B] hover:bg-[#451759] text-white font-bold text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-between group"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <MessageSquare className="w-5 h-5 fill-white" />
                                        <div>
                                            <div className="text-sm font-bold">Enviar a la persona que va a dejarlo</div>
                                            <div className="text-[11px] font-normal text-white/90">
                                                {mensajeroNombre} {mensajeroTelefono ? `(${mensajeroTelefono})` : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </a>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => window.print()}
                                    className="flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors text-xs font-semibold"
                                    style={{
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                        color: isDark ? '#ffffff' : '#374151'
                                    }}
                                >
                                    <Printer className="w-4 h-4" />
                                    Imprimir Comprobante
                                </button>
                                <button
                                    onClick={onClose}
                                    className="flex-1 py-3 px-4 bg-gradient-to-r from-[#61257B] to-[#C1348B] text-white font-bold text-xs rounded-xl shadow-md hover:shadow-lg hover:shadow-purple-900/30 transition-all"
                                >
                                    Listo, Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* VISTA 2: FORMULARIO */
                    <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1 custom-scroll">
                        {error && (
                            <div 
                                className="p-3 rounded-xl text-xs flex items-center gap-2 border"
                                style={{
                                    background: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                                    borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : '#fecaca',
                                    color: isDark ? '#fca5a5' : '#b91c1c'
                                }}
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* SECCIÓN 0: MIS DATOS (AUTO-LLENADOS) */}
                        <div 
                            className="p-3.5 rounded-2xl space-y-2 border"
                            style={{
                                background: isDark ? 'rgba(97, 37, 123, 0.15)' : 'rgba(245, 243, 255, 0.8)',
                                borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#e9d5ff'
                            }}
                        >
                            <span className="text-xs font-bold flex items-center gap-1.5 uppercase tracking-wider" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>
                                <UserCheck className="w-4 h-4 text-[#C1348B]" />
                                Tus Datos (Cliente Titular)
                            </span>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                <div>
                                    <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Nombre:</span>
                                    <p className="font-bold" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>{miNombreCompleto || user?.email}</p>
                                </div>
                                <div>
                                    <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Correo:</span>
                                    <p className="font-medium truncate" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>{user?.email}</p>
                                </div>
                                <div>
                                    <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Teléfono:</span>
                                    <p className="font-medium" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>{user?.telefono || '—'}</p>
                                </div>
                                <div>
                                    <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>CI / NIT:</span>
                                    <p className="font-medium" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>{user?.ci_nit || '—'}</p>
                                </div>
                            </div>
                        </div>

                        {/* SECCIÓN 1: ¿QUIÉN DEJA EL PAQUETE? */}
                        <div className="space-y-3 pt-1 border-t" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}>
                            <label className="block text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                <ArrowRight className="w-4 h-4 text-amber-500" />
                                1. ¿Quién dejará el paquete en recepción? <span className="text-red-500">*</span>
                            </label>

                            <div
                                className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                                    yoLoDejoPersonalmente 
                                        ? (isDark ? 'bg-emerald-950/40 border-emerald-500/50' : 'bg-emerald-50 border-emerald-300')
                                        : (isDark ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                                }`}
                                onClick={() => setYoLoDejoPersonalmente(true)}
                            >
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                        yoLoDejoPersonalmente 
                                            ? 'border-emerald-500 bg-emerald-500' 
                                            : 'border-gray-400'
                                    }`}>
                                        {yoLoDejoPersonalmente && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold block" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>Yo personalmente lo dejaré en recepción</span>
                                        <span className="text-[10px]" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                            Irás tú mismo a entregar el paquete a recepción
                                        </span>
                                    </div>
                                </label>
                            </div>

                            <div
                                className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                                    !yoLoDejoPersonalmente 
                                        ? (isDark ? 'bg-amber-950/40 border-amber-500/50' : 'bg-amber-50 border-amber-300')
                                        : (isDark ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                                }`}
                                onClick={() => setYoLoDejoPersonalmente(false)}
                            >
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                        !yoLoDejoPersonalmente 
                                            ? 'border-amber-500 bg-amber-500' 
                                            : 'border-gray-400'
                                    }`}>
                                        {!yoLoDejoPersonalmente && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold block" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>Mandaré a una persona externa / mensajero</span>
                                        <span className="text-[10px]" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                            Enviarás a un delivery, chofer, familiar o colega a dejar el paquete
                                        </span>
                                    </div>
                                </label>
                            </div>

                            {/* Datos de la persona que mandas a dejar el paquete */}
                            {!yoLoDejoPersonalmente && (
                                <div 
                                    className="p-3.5 rounded-2xl space-y-3 animate-fade-in border"
                                    style={{
                                        background: isDark ? 'rgba(234, 132, 6, 0.12)' : 'rgba(254, 243, 199, 0.7)',
                                        borderColor: isDark ? 'rgba(234, 132, 6, 0.3)' : '#fde68a'
                                    }}
                                >
                                    <span className="text-xs font-semibold block flex items-center gap-1.5" style={{ color: isDark ? '#ffd97c' : '#92400e' }}>
                                        <User className="w-3.5 h-3.5" />
                                        Datos de la persona que mandas a dejar el paquete:
                                    </span>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                                Nombre Completo <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={mensajeroNombre}
                                                onChange={(e) => setMensajeroNombre(e.target.value)}
                                                placeholder="Ej: Pedro Chofer / Moto Delivery"
                                                className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none"
                                                style={{
                                                    background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                                    color: isDark ? '#ffffff' : '#111827',
                                                    borderColor: isDark ? 'rgba(234, 132, 6, 0.4)' : '#fcd34d'
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                                Celular (Opcional)
                                            </label>
                                            <input
                                                type="text"
                                                value={mensajeroTelefono}
                                                onChange={(e) => setMensajeroTelefono(e.target.value)}
                                                placeholder="70012345"
                                                className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none"
                                                style={{
                                                    background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                                    color: isDark ? '#ffffff' : '#111827',
                                                    borderColor: isDark ? 'rgba(234, 132, 6, 0.4)' : '#fcd34d'
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                                CI / NIT (Opcional)
                                            </label>
                                            <input
                                                type="text"
                                                value={mensajeroCiNit}
                                                onChange={(e) => setMensajeroCiNit(e.target.value)}
                                                placeholder="Carnet de identidad"
                                                className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none"
                                                style={{
                                                    background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                                    color: isDark ? '#ffffff' : '#111827',
                                                    borderColor: isDark ? 'rgba(234, 132, 6, 0.4)' : '#fcd34d'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SECCIÓN 2: ¿QUIÉN VA A RECOGER? (DESTINATARIO) */}
                        <div className="space-y-3 pt-2 border-t" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}>
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: isDark ? '#ffffff' : '#374151' }}>
                                <Send className="w-4 h-4 text-[#C1348B]" />
                                2. Persona que va a recoger el paquete (Destinatario) <span className="text-red-500">*</span>
                            </div>
                            <div 
                                className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3.5 rounded-2xl border"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'
                                }}
                            >
                                <div>
                                    <label className="block text-xs font-medium mb-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                        Nombre Completo de quien recogerá <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={destNombre}
                                        onChange={(e) => setDestNombre(e.target.value)}
                                        required
                                        placeholder="Ej: Lic. Marcelo Suárez"
                                        className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] outline-none"
                                        style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                            color: isDark ? '#ffffff' : '#111827',
                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1 flex items-center gap-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                        <MessageSquare className="w-3.5 h-3.5 text-emerald-500" />
                                        Celular / WhatsApp (para enviarle PIN de retiro)
                                    </label>
                                    <input
                                        type="text"
                                        value={destTelefono}
                                        onChange={(e) => setDestTelefono(e.target.value)}
                                        placeholder="Ej: 70012345"
                                        className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] outline-none"
                                        style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                            color: isDark ? '#ffffff' : '#111827',
                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1" style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>CI / NIT de quien recogerá (Opcional)</label>
                                    <input
                                        type="text"
                                        value={destCiNit}
                                        onChange={(e) => setDestCiNit(e.target.value)}
                                        placeholder="Carnet para validación"
                                        className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] outline-none"
                                        style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                            color: isDark ? '#ffffff' : '#111827',
                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1" style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>Correo Electrónico (Opcional)</label>
                                    <input
                                        type="email"
                                        value={destEmail}
                                        onChange={(e) => setDestEmail(e.target.value)}
                                        placeholder="correo@ejemplo.com"
                                        className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] outline-none"
                                        style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                            color: isDark ? '#ffffff' : '#111827',
                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* SECCIÓN 3: DESCRIPCIÓN */}
                        <div className="space-y-3 pt-2 border-t" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1" style={{ color: isDark ? '#ffffff' : '#374151' }}>
                                    <FileText className="w-3.5 h-3.5 text-[#C1348B]" /> 3. Descripción de la Encomienda
                                </label>
                                <textarea
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                    rows={3}
                                    placeholder="Ej: Caja mediana de repuestos, sobre manila con contratos, paquetes sellados..."
                                    className="w-full px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] outline-none resize-none"
                                    style={{
                                        background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                        color: isDark ? '#ffffff' : '#111827',
                                        borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db'
                                    }}
                                />
                            </div>
                            <div 
                                className="p-3 rounded-xl text-[11px] border"
                                style={{
                                    background: isDark ? 'rgba(97, 37, 123, 0.15)' : 'rgba(245, 243, 255, 0.7)',
                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe',
                                    color: isDark ? '#ffd97c' : '#61257B'
                                }}
                            >
                                💡 <strong>Nota:</strong> Cuando el paquete llegue físicamente a recepción, el personal de Comarca tomará la fotografía de evidencia de custodia y la podrás ver en tu seguimiento.
                            </div>
                        </div>

                        {/* BOTONES */}
                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 py-3 px-4 border rounded-xl text-xs font-semibold transition-colors"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db',
                                    color: isDark ? '#cbd5e1' : '#4b5563'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-[#61257B] to-[#C1348B] text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg hover:shadow-purple-900/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Registrando encomienda...
                                    </>
                                ) : (
                                    <>
                                        <Package className="w-4 h-4" />
                                        Registrar Encomienda
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>,
        document.body
    );
};

export default RegistrarEncomiendaClienteModal;
